import { FunctionComponent, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import { CreateTransactionModalFormModel } from './CreateTransactionModal/CreateTransactionModalFormModel';
import { TransactionFromLocationField } from './form-fields/TransactionFromLocationField';
import { TransactionToLocationField } from './form-fields/TransactionToLocationField';
import { TransactionDateField } from './form-fields/TransactionDateField';
import { TransactionCarrierField } from './form-fields/TransactionCarrierField';
import { TransactionDocumentNumberField } from './form-fields/TransactionDocumentNumberField';
import { TransactionReferenceNumberField } from './form-fields/TransactionReferenceNumberField';
import { TransactionNotesField } from './form-fields/TransactionNotesField';
import { TransactionAssetAmountField } from './form-fields/TransactionAssetAmountField';
import { transactionService } from '../api/transactions/transaction.service';
import { TransactionType } from '../api/transactions/transaction.contracts';
import { toast } from 'react-toastify';
import { useTranslation } from '@/lib';
import { ApiResponseCode } from '@/lib/api/api-response-code';
import { AccordionSection } from '@/components/accordion';
import { TransactionProvider, useTransactionContext } from '../context/TransactionContext';
import { ConfirmationDialog } from '@/components/modals/ConfirmationDialog';

interface GuestCreateTransactionModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  transactionType: TransactionType;
}

interface GuestCreateTransactionModalContentProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  transactionType: TransactionType;
}

const GuestCreateTransactionModalContent: FunctionComponent<GuestCreateTransactionModalContentProps> = ({
  open,
  onClose,
  onSuccess,
  transactionType,
}) => {
  const { t } = useTranslation();
  const [formKey, setFormKey] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const { form, resetForm, fieldArray, hasAvailableAssetTypes } = useTransactionContext();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  const { fields, append, remove } = fieldArray;

  const saveTransaction = async (data: CreateTransactionModalFormModel) => {
    try {
      // Validation ensures these are defined, but TypeScript doesn't know that
      if (!data.fromLocationId || !data.toLocationId) {
        console.error('Location IDs are required');
        return;
      }

      // Filter out assets that don't have an assetTypeId selected and map to CreateTransactionDetailDTO
      const details =
        data.assets
          ?.filter((asset) => asset.assetTypeId && asset.assetTypeId > 0)
          .map((asset) => ({
            assetTypeId: asset.assetTypeId,
            amount: asset.amount,
          })) || [];

      const response = await transactionService.create({
        type: transactionType,
        toLocationId: data.toLocationId,
        fromLocationId: data.fromLocationId,
        transactionDate: data.date.toISOString(),
        carrierId: data.carrierId,
        documentNumber: data.documentNumber || '',
        referenceNumber: data.referenceNumber || '',
        notes: data.notes || '',
        details: details,
      });

      if (!response.isSuccess) {
        if (response.responseCode === ApiResponseCode.DUPLICATE_ENTITY_CODE) {
          toast.error(t('error_creating_transaction'));
        } else {
          toast.error(t('error_creating_transaction'));
        }
        return;
      }

      toast.success(t('transaction_created'));

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error creating transaction:', error);
      toast.error(t('error_creating_transaction'));
    }
  };

  const onCreateSubmit = async (data: CreateTransactionModalFormModel) => {
    await saveTransaction(data);

    resetForm();
    onClose();
  };

  const onCreateAndNewSubmit = async (data: CreateTransactionModalFormModel) => {
    await saveTransaction(data);
    resetForm();
    // Increment key to trigger animation
    setFormKey((prev) => prev + 1);
  };

  const handleCloseAttempt = () => {
    // Check if form has unsaved changes
    if (isDirty) {
      setShowConfirmDialog(true);
    } else {
      handleClose();
    }
  };

  const handleClose = () => {
    resetForm();
    setShowConfirmDialog(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  // Get the transaction type name for the title
  const getTransactionTypeName = () => {
    switch (transactionType) {
      case TransactionType.Transfer:
        return t('transfer');
      case TransactionType.Correction:
        return t('correction');
      default:
        return t('transaction');
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseAttempt}
        maxWidth="lg"
        fullWidth
        disableRestoreFocus
        slotProps={{
          paper: { className: 'bg-gray-50 dark:bg-gray-900' },
        }}
      >
        <DialogTitle className="px-6 pt-4 pb-2">
          {t('create')} {getTransactionTypeName()}
        </DialogTitle>
        <form onSubmit={handleSubmit(onCreateSubmit)}>
          <DialogContent
            key={formKey}
            className="px-6 pt-2 animate-slideIn"
            sx={{
              '@keyframes slideIn': {
                '0%': {
                  opacity: 0,
                  transform: 'translateX(20px)',
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateX(0)',
                },
              },
              animation: formKey > 0 ? 'slideIn 0.3s ease-out' : 'none',
            }}
          >
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <TransactionFromLocationField control={control} errors={errors} autoFocus />
                <TransactionToLocationField control={control} errors={errors} />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <TransactionDateField control={control} errors={errors} />
              </div>

              <AccordionSection title={t('additional_details')} defaultExpanded={true}>
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <TransactionDocumentNumberField control={control} errors={errors} />
                    <TransactionReferenceNumberField control={control} errors={errors} />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <TransactionCarrierField control={control} errors={errors} />
                    <TransactionNotesField control={control} errors={errors} />
                  </div>
                </div>
              </AccordionSection>

              <AccordionSection title={t('assets')} defaultExpanded={true}>
                <div className="flex flex-col">
                  {fields.map((field, index) => (
                    <TransactionAssetAmountField
                      key={field.id}
                      control={control}
                      errors={errors}
                      index={index}
                      onRemove={() => remove(index)}
                      canRemove={fields.length > 1}
                    />
                  ))}

                  <div className="mt-2">
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() => append({ assetTypeId: undefined, amount: 1 })}
                      size="small"
                      disabled={!hasAvailableAssetTypes}
                    >
                      {t('add_asset')}
                    </Button>
                  </div>
                </div>
              </AccordionSection>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAttempt} disabled={isSubmitting} variant="outlined">
              {t('cancel')}
            </Button>
            <Button
              variant="outlined"
              loading={isSubmitting}
              disabled={isSubmitting}
              onClick={() => {
                handleSubmit(onCreateAndNewSubmit)();
              }}
            >
              {t('save_and_create_new')}
            </Button>
            <Button type="submit" variant="contained" loading={isSubmitting} disabled={isSubmitting}>
              {t('create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showConfirmDialog}
        onClose={handleCancelClose}
        onConfirm={handleClose}
        title={t('unsaved_changes')}
        message={t('unsaved_changes_message')}
        confirmText={t('discard_changes')}
      />
    </>
  );
};

export const GuestCreateTransactionModal: FunctionComponent<GuestCreateTransactionModalProps> = (props) => {
  return (
    <TransactionProvider open={props.open} isGuestMode={true}>
      <GuestCreateTransactionModalContent {...props} />
    </TransactionProvider>
  );
};
